/* eslint-disable @typescript-eslint/naming-convention */
import { Box, Button, Group, Stack, Text } from '@mantine/core'
import type { UseFormReturnType } from '@mantine/form'
import { isEmail, useForm } from '@mantine/form'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { useMediaQuery } from '@mantine/hooks'
import { Source, Visitor } from 'api/dto/visitor'
import { useTradeshow } from 'api/query/tradeshow'
import { useTradeshowPrices } from 'api/query/price'
import { redirectToCheckout } from 'utils/stripe'
import { showNotification } from '@mantine/notifications'
import { Loading } from 'components/loading/loading'
import { EventEnded } from 'components/event-end/event-ended'
import { isTradeshowEnded } from 'utils/tradeshow'

import type { RegisteredVisitor } from './types'
import { RegistrationHeader } from './components/registration-header'
import { EventInfo } from './components/event-info'
import { PersonalInfoSection } from './components/personal-info-section'
import { TicketSelectionSection } from './components/ticket-selection-section'
import { CompanionDetailsSection } from './components/companion-details-section'
import { AdditionalAttendeeSection } from './components/additional-attendee-section'
import { useVisitorRegistrationForm } from './hooks/use-visitor-registration-form'
import { ValidationError } from './components/validation-error'

export const RegisterNewVisitor = () => {
  const {
    t,
    i18n: { changeLanguage, language },
  } = useTranslation()
  const { tradeshowId } = useParams()
  const { data: tradeshow, isLoading: isPending, isSuccess } = useTradeshow(tradeshowId)
  const [isLoading, setIsLoading] = useState(false)
  const isSmallDevice = useMediaQuery('(max-width: 864px)')
  const currentLanguage = language.split('-')[0]

  const eventEnded = tradeshow ? isTradeshowEnded(tradeshow) : false

  const form = useForm<RegisteredVisitor>({
    initialValues: {
      hasConsent: false,
      addons: [],
      companions: [],
      price: '',
      numberOfTickets: 1,
      additionalVisitors: [],
      address: {
        street: '',
        city: '',
        province: '',
        postalCode: '',
        country: '',
      },
    },
    validate: {
      firstName: (value) => (value ? null : <ValidationError message={t('common.invalid-first-name')} />),
      lastName: (value) => (value ? null : <ValidationError message={t('common.invalid-last-name')} />),
      email: isEmail(<ValidationError message={t('common.invalid-email')} />),
      companyName: (value) => (value ? null : <ValidationError message={t('common.invalid-company')} />),
      price: (value) => (value && value !== '' ? null : <ValidationError message={t('common.invalid-price')} />),
      address: {
        street: (value: string) => (value ? null : <ValidationError message={t('common.invalid-street')} />),
        city: (value: string) => (value ? null : <ValidationError message={t('common.invalid-city')} />),
        province: (value: string) => (value ? null : <ValidationError message={t('common.invalid-province')} />),
        postalCode: (value: string) => (value ? null : <ValidationError message={t('common.invalid-postal-code')} />),
        country: (value: string) => (value ? null : <ValidationError message={t('common.invalid-country')} />),
      },
      additionalVisitors: {
        firstName: (value: string) => (value ? null : <ValidationError message={t('common.invalid-first-name')} />),
        lastName: (value: string) => (value ? null : <ValidationError message={t('common.invalid-last-name')} />),
        email: isEmail(<ValidationError message={t('common.invalid-email')} />),
        companyName: (value: string) => (value ? null : <ValidationError message={t('common.invalid-company')} />),
        companyPhone: (value: string) => (value ? null : <ValidationError message={t('common.field-required')} />),
        price: (value: string) => (value && value !== '' ? null : <ValidationError message={t('common.invalid-price')} />),
        address: {
          street: (value: string) => (value ? null : <ValidationError message={t('common.invalid-street')} />),
          city: (value: string) => (value ? null : <ValidationError message={t('common.invalid-city')} />),
          province: (value: string) => (value ? null : <ValidationError message={t('common.invalid-province')} />),
          postalCode: (value: string) => (value ? null : <ValidationError message={t('common.invalid-postal-code')} />),
          country: (value: string) => (value ? null : <ValidationError message={t('common.invalid-country')} />),
        },
      },
    },
  })

  async function onSubmit(values: RegisteredVisitor) {
    setIsLoading(true)

    try {
      const { price, addons, companions, address, additionalVisitors = [], ...rest } = values

      const visitor = new Visitor({
        ...rest,
        address,
        tradeShowId: tradeshowId!,
        importedFrom: Source.DIRECT,
      } as Visitor)

      visitor.companions = companions
      visitor.additionalVisitors = additionalVisitors
      visitor.price = price
      visitor.addons = addons

      await redirectToCheckout(tradeshowId!, visitor, visitor.email)
      // Note: redirectToCheckout navigates away via window.location.href, code below won't run on success
    } catch (error) {
      console.error('Checkout error:', error)
      showNotification({
        title: t('common.error.title'),
        message: t('common.error.checkout-failed'),
        color: 'red',
      })
      setIsLoading(false)
    }
  }

  if (isPending) {
    return <Loading />
  }

  if (!isSuccess) {
    showNotification({
      title: t('common.error.title'),
      message: t('common.error.message'),
      color: 'red',
    })

    return null
  }

  if (eventEnded) {
    return <EventEnded />
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%)',
      }}
    >
      <RegistrationHeader isSmallDevice={isSmallDevice} currentLanguage={currentLanguage} onLanguageChange={() => changeLanguage(currentLanguage === 'fr' ? 'en' : 'fr')} />

      <Box
        sx={{
          maxWidth: 900,
          margin: '0 auto',
          padding: isSmallDevice ? '24px 16px' : '40px 32px',
        }}
      >
        <EventInfo tradeshow={tradeshow} isSmallDevice={isSmallDevice} currentLanguage={currentLanguage} />

        <RegisterNewVisitorForm form={form} onSubmit={onSubmit} isLoading={isLoading} isSmallDevice={isSmallDevice} hasFoodIncluded={tradeshow.config.hasFoodIncluded} />
      </Box>
    </Box>
  )
}

interface RegisterNewVisitorFormProps {
  form: UseFormReturnType<RegisteredVisitor, (values: RegisteredVisitor) => RegisteredVisitor>
  onSubmit: (values: RegisteredVisitor) => Promise<void>
  isLoading: boolean
  hasFoodIncluded: boolean
  isSmallDevice: boolean
}

function RegisterNewVisitorForm({ form, isLoading, onSubmit, isSmallDevice, hasFoodIncluded }: RegisterNewVisitorFormProps) {
  const { t } = useTranslation()
  const { tradeshowId } = useParams()
  const { data, isLoading: isLoadingPrices, isSuccess } = useTradeshowPrices(tradeshowId)

  const prices = data?.filter((price) => price.isTicket) || []
  const addons = data?.filter((price) => price.isAddon) || []
  const companions = data?.filter((price) => price.isCompanionPrice) || []
  const selectedPrice = prices.find((price) => price.id === form.values.price)

  const { showCompanion, handleQuantityChange, handleMainAddonToggle, handleAttendeeAddonToggle } = useVisitorRegistrationForm({
    form,
    prices,
    isLoadingPrices,
    isSuccess,
  })

  if (isLoadingPrices) {
    return <Loading />
  }

  if (!isSuccess) {
    return null
  }

  return (
    <form onSubmit={form.onSubmit(onSubmit)}>
      <Stack spacing={24}>
        <PersonalInfoSection form={form} isSmallDevice={isSmallDevice} hasFoodIncluded={hasFoodIncluded} />

        <TicketSelectionSection
          form={form}
          isSmallDevice={isSmallDevice}
          prices={prices}
          addons={addons}
          companions={companions}
          selectedPrice={selectedPrice}
          onMainAddonToggle={handleMainAddonToggle}
          onQuantityChange={handleQuantityChange}
        />

        {showCompanion && <CompanionDetailsSection form={form} isSmallDevice={isSmallDevice} />}

        {form.values.additionalVisitors && form.values.additionalVisitors.length > 0 && (
          <Stack spacing={16}>
            {form.values.additionalVisitors.map((visitor, index) => (
              <AdditionalAttendeeSection
                key={index}
                form={form}
                isSmallDevice={isSmallDevice}
                hasFoodIncluded={hasFoodIncluded}
                visitor={visitor}
                index={index}
                prices={prices}
                addons={addons}
                onAddonToggle={handleAttendeeAddonToggle}
              />
            ))}
          </Stack>
        )}

        <Group position="right">
          <Button
            type="submit"
            size="lg"
            radius="md"
            loading={isLoading}
            disabled={isLoading || !form.isValid()}
            sx={{
              background: '#850014',
              fontWeight: 500,
              fontSize: 15,
              height: 48,
              paddingLeft: 32,
              paddingRight: 32,
              '&:hover': { background: '#6b0010' },
              '&:disabled': { background: '#d1d5db' },
            }}
          >
            {t('authentication.register')}
          </Button>
        </Group>
      </Stack>
    </form>
  )
}
